import { slugifyFilenamePart } from './logExport';

/**
 * @param {HTMLElement} element
 * @param {string} filename e.g. widget.png
 */
export async function captureElementToPngDownload(element, filename) {
  const html2canvas = (await import('html2canvas')).default;
  const canvas = await html2canvas(element, {
    scale: Math.min(2, (window.devicePixelRatio || 1) * 1.5),
    useCORS: true,
    allowTaint: true,
    logging: false,
    backgroundColor: '#ffffff'
  });
  await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Could not create image'));
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename.endsWith('.png') ? filename : `${filename}.png`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        resolve();
      },
      'image/png',
      0.92
    );
  });
}

/**
 * Capture each `.commray-widget-capture` under root (depth-first).
 * @param {HTMLElement | null} root
 * @param {string} dashboardBaseName
 * @param {(msg: string) => void} [onProgress]
 */
export async function exportWidgetImagesFromRoot(root, dashboardBaseName, onProgress) {
  if (!root) throw new Error('Nothing to capture');
  const widgets = Array.from(root.querySelectorAll('.commray-widget-capture'));
  if (widgets.length === 0) throw new Error('No widgets on this dashboard');
  const base = slugifyFilenamePart(dashboardBaseName);
  for (let i = 0; i < widgets.length; i += 1) {
    const el = widgets[i];
    const titleEl = el.querySelector('.widget-title-right h3, .widget-header h3');
    const title = titleEl?.textContent?.trim() || `widget-${i + 1}`;
    const part = slugifyFilenamePart(title);
    if (onProgress) onProgress(`Capturing ${i + 1}/${widgets.length}: ${title}`);
    // Sequential: avoids starving the main thread and overlapping canvas work
    // eslint-disable-next-line no-await-in-loop
    await captureElementToPngDownload(el, `${base}-${part || `widget-${i + 1}`}.png`);
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 150));
  }
}

/**
 * Single PNG of the whole grid area.
 */
export async function exportDashboardGridPng(gridEl, dashboardBaseName) {
  if (!gridEl) throw new Error('Nothing to capture');
  const base = slugifyFilenamePart(dashboardBaseName);
  await captureElementToPngDownload(gridEl, `${base}-dashboard.png`);
}
